import { type MenuElement } from "prosemirror-menu";
import type { EditorState } from "prosemirror-state";

interface Panel {
  update: (state: EditorState) => boolean;
  focusables: HTMLElement[];
}
export type PanelType = "main" | "moreOptions" | "textStyle" | "textColor" | "characterLimit";
export type PanelContent = readonly (readonly MenuElement[])[];

/**
 * Returns the index of the next visible focusable in `focusables` starting from
 * `startIndex + delta`, wrapping around. Returns null if none are visible.
 *
 * Copied from prosemirror-menu/src/menu.ts (MIT):
 * Copyright (C) 2015-2017 by Marijn Haverbeke <marijn@haverbeke.berlin> and others
 * https://github.com/prosemirror/prosemirror-menu
 */
function findFocusableIndex(
  focusables: readonly HTMLElement[],
  startIndex: number,
  delta: 1 | -1,
): number | null {
  const { length } = focusables;
  for (let i = 0, index = startIndex + delta; ; index += delta, i += 1) {
    const normIndex = ((index % length) + length) % length;
    if (focusables[normIndex].style.display !== "none") return normIndex;
    if (i === length) return null;
  }
}

export class PanelView {
  private focusables: HTMLElement[];

  private focusIndex = 0;

  private readonly contentUpdate: (state: EditorState) => boolean;

  constructor(rendered: Panel) {
    this.contentUpdate = rendered.update;
    this.focusables = rendered.focusables;

    // Roving tabindex: first focusable gets tabindex=0, rest -1.
    this.focusables.forEach((el, i) => {
      if (i > 0) el.setAttribute("tabindex", "-1");
      el.addEventListener("focus", () => {
        if (this.focusIndex === i) return;
        this.focusables[this.focusIndex].setAttribute("tabindex", "-1");
        this.focusIndex = i;
        el.setAttribute("tabindex", "0");
      });
    });
  }

  update(state: EditorState): void {
    this.contentUpdate(state);
  }

  handleKeydown(event: KeyboardEvent): void {
    let move: number | null = null;
    if (event.key === "ArrowRight") move = findFocusableIndex(this.focusables, this.focusIndex, 1);
    else if (event.key === "ArrowLeft")
      move = findFocusableIndex(this.focusables, this.focusIndex, -1);
    else if (event.key === "Home") move = findFocusableIndex(this.focusables, -1, 1);
    else if (event.key === "End")
      move = findFocusableIndex(this.focusables, this.focusables.length, -1);
    if (move == null) return;
    event.preventDefault();
    event.stopPropagation();
    this.setFocusIndex(move);
  }

  setFocus(): void {
    this.getCurrentFocusable()?.focus({ focusVisible: true });
  }

  private getCurrentFocusable(): HTMLElement | undefined {
    return this.focusables[this.focusIndex];
  }

  private setFocusIndex(index: number): void {
    if (this.focusables.length <= 1) return;
    this.focusables[this.focusIndex].setAttribute("tabindex", "-1");
    this.focusIndex = index;
    const next = this.focusables[index];
    next.setAttribute("tabindex", "0");
    next.focus();
  }
}
